/**
 * FirstChallengeTourOverlay — the interactive (variant 1a) coach-mark overlay for the first-challenge
 * tour. Always mounted; hosts the controller (derives the step from live app state), then renders the
 * glowing SpotlightRing + anchored CoachCard over the REAL screens. Non-blocking: `box-none` lets real
 * taps through; only the card buttons (and the coins tap-block) capture touches. Skip/Resume/Got-it and
 * the closing "wrap" card live here. See specs/first-challenge-onboarding/spec.md.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { View, Text, Pressable, StyleSheet, useWindowDimensions, AccessibilityInfo } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { usePathname, useRouter, type Href } from 'expo-router';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { useFirstChallengeTourStore } from '../../../src/onboarding/stores/firstChallengeTourStore';
import { deriveTourStep } from '../../../src/onboarding/helpers/firstChallengeTourStep.helper';
import { markFirstChallengeTourSeen } from '../../../src/onboarding/helpers/firstChallengeTourSeen.helper';
import { FC_WRAP_GUEST_CTA } from '../../../src/onboarding/constants/firstChallengeTour.constants';
import { useAnalytics } from '../../../src/analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../../src/analytics';
import { buildPublicSignInHref } from '../../../src/signIn/helpers/postAuthRedirect.helper';
import { CoachCard } from '../CoachCard/CoachCard';
import { SpotlightRing } from '../SpotlightRing/SpotlightRing';
import { COACH_CARD_WIDTH, COACH_COPY, COACH_TEST_IDS } from '../CoachCard/CoachCard.constants';
import { AnimatedMonkey } from '../../onboarding/AnimatedMonkey/AnimatedMonkey';
import { WrapUpsellCard } from './WrapUpsellCard';

/** Anchor the card just outside the target rect, choosing below/above by available room. */
function cardLayout(
  rect: { x: number; y: number; width: number; height: number } | null,
  winW: number,
  winH: number,
): { position: object; arrow: 'up' | 'down' | 'none'; arrowOffset?: number } {
  if (!rect) {
    return { position: { bottom: 96, left: Math.max(10, (winW - COACH_CARD_WIDTH) / 2) }, arrow: 'none' };
  }
  const cx = rect.x + rect.width / 2;
  const left = Math.min(Math.max(cx - COACH_CARD_WIDTH / 2, 10), winW - 10 - COACH_CARD_WIDTH);
  const placeBelow = rect.y + rect.height < winH * 0.6;
  const arrowOffset = Math.min(Math.max(cx - left - 8, 16), COACH_CARD_WIDTH - 34);
  return placeBelow
    ? { position: { top: rect.y + rect.height + 12, left }, arrow: 'up', arrowOffset }
    : { position: { bottom: winH - rect.y + 12, left }, arrow: 'down', arrowOffset };
}

export function FirstChallengeTourOverlay() {
  const route = usePathname();
  const router = useRouter();
  const { track } = useAnalytics();
  const { width: winW, height: winH } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const { user, isAnonymous } = useSupabaseAuth();

  const active = useFirstChallengeTourStore((s) => s.active);
  const skipped = useFirstChallengeTourStore((s) => s.skipped);
  const beat = useFirstChallengeTourStore((s) => s.beat);
  const createStep = useFirstChallengeTourStore((s) => s.createStep);
  const pickCount = useFirstChallengeTourStore((s) => s.pickCount);
  const sent = useFirstChallengeTourStore((s) => s.sent);
  const targetRect = useFirstChallengeTourStore((s) => s.targetRect);
  const pendingStart = useFirstChallengeTourStore((s) => s.pendingStart);

  const reduceMotion = useRef(false);
  useEffect(() => {
    void AccessibilityInfo.isReduceMotionEnabled().then((v) => { reduceMotion.current = v; });
  }, []);

  // Start the tour once the questionnaire hand-off lands us on the Arena.
  useEffect(() => {
    if (pendingStart && route.includes('/app/arena')) {
      useFirstChallengeTourStore.getState().startTour();
    }
  }, [pendingStart, route]);

  const derived = useMemo(
    () => (active ? deriveTourStep({ route, createStep, pickCount, sent, beat }) : null),
    [active, route, createStep, pickCount, sent, beat],
  );

  // Publish the derived step to the store (target hooks + ring read it) and reset the beat on a
  // screen change so multi-beat screens always start at their first beat.
  const prevScreen = useRef<string | null>(null);
  useEffect(() => {
    if (!active) return;
    const store = useFirstChallengeTourStore.getState();
    const nextStep = derived?.step ?? null;
    if (store.step !== nextStep) store.setStep(nextStep);
    const screen = derived?.screen ?? null;
    if (screen !== prevScreen.current) {
      prevScreen.current = screen;
      if (store.beat !== 0) store.setBeat(0);
    }
    if (nextStep == null && store.targetRect) store.setTargetRect(null);
  }, [active, derived?.step, derived?.screen]);

  const markSeen = useCallback(() => {
    if (user?.id) void markFirstChallengeTourSeen(user.id);
  }, [user?.id]);

  const onSkip = useCallback(() => {
    useFirstChallengeTourStore.getState().skip();
    markSeen();
  }, [markSeen]);

  const onResume = useCallback(() => {
    useFirstChallengeTourStore.getState().resume();
  }, []);

  const onGotIt = useCallback(() => {
    useFirstChallengeTourStore.getState().advanceBeat();
  }, []);

  const onFinishWrap = useCallback(() => {
    markSeen();
    useFirstChallengeTourStore.getState().dismiss();
  }, [markSeen]);

  // Guest-only: the "save your progress" sign-up CTA on the closing wrap card — the true end of
  // onboarding, and the honest place to ask (the anonymous account upgrades in place, so their
  // just-made pick is already theirs and they jump to 1000 coins).
  const onWrapSignUp = useCallback(() => {
    track(ANALYTICS_EVENTS.GUEST_SIGNUP_PROMPT_ACCEPTED, { action: FC_WRAP_GUEST_CTA.action });
    markSeen();
    useFirstChallengeTourStore.getState().dismiss();
    router.push(buildPublicSignInHref({ signup: true, returnUrl: '/app/arena' }) as Href);
  }, [track, markSeen, router]);

  // Fire the impression once when the wrap card is shown to a guest (mirrors GuestBanner tracking).
  const isWrapForGuest = active && !skipped && derived?.step === 'wrap' && isAnonymous;
  const wrapShownRef = useRef(false);
  useEffect(() => {
    if (isWrapForGuest && !wrapShownRef.current) {
      wrapShownRef.current = true;
      track(ANALYTICS_EVENTS.GUEST_SIGNUP_PROMPT_SHOWN, { action: FC_WRAP_GUEST_CTA.action });
    }
  }, [isWrapForGuest, track]);

  if (!active) return null;

  // Skipped: only the Resume chip.
  if (skipped) {
    return (
      <View style={styles.fill} pointerEvents="box-none">
        <Pressable onPress={onResume} testID={COACH_TEST_IDS.RESUME} style={styles.resumeChip}>
          <Text style={styles.resumeText}>{COACH_COPY.resume}</Text>
        </Pressable>
      </View>
    );
  }

  // Off-path or the auto celebrate beat: show nothing (the real SuccessModal handles celebrate).
  if (!derived || derived.step === 'celebrate') return null;

  // Closing "wrap" card (full-screen) — guest gets the sign-up upsell, real user just continues.
  if (derived.step === 'wrap') {
    return (
      <WrapUpsellCard
        isGuest={isAnonymous}
        title={derived.title}
        text={derived.text}
        onSignUp={onWrapSignUp}
        onContinue={onFinishWrap}
      />
    );
  }

  // The bottom-nav step has no measurable ref (expo-router owns the tab bar), so synthesize its rect.
  const navRect =
    derived.step === 'nav'
      ? { x: 8, y: winH - (64 + insets.bottom), width: winW - 16, height: 60 + insets.bottom }
      : null;
  const effectiveRect = navRect ?? targetRect;
  const layout = cardLayout(effectiveRect, winW, winH);
  const isCoins = derived.step === 'coins';

  return (
    <View style={styles.fill} pointerEvents="box-none">
      <SpotlightRing rect={effectiveRect} reduceMotion={reduceMotion.current} />

      {/* Coins beat: a transparent tap-block over the stepper so the stake can't be changed. */}
      {isCoins && effectiveRect ? (
        <View
          pointerEvents="auto"
          style={{ position: 'absolute', left: effectiveRect.x, top: effectiveRect.y, width: effectiveRect.width, height: effectiveRect.height }}
        />
      ) : null}

      <CoachCard
        title={derived.title}
        text={derived.text}
        progress={derived.progress}
        beatType={derived.beatType === 'coach' ? 'coach' : 'action'}
        arrow={layout.arrow}
        arrowOffset={layout.arrowOffset}
        avatar={<AnimatedMonkey size={34} />}
        onSkip={onSkip}
        onGotIt={onGotIt}
        reduceMotion={reduceMotion.current}
        style={{ position: 'absolute', ...layout.position }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  fill: { ...StyleSheet.absoluteFillObject, zIndex: 1000, elevation: 1000 },
  resumeChip: {
    position: 'absolute', bottom: 80, right: 16,
    backgroundColor: '#442698', borderRadius: 9999, paddingVertical: 11, paddingHorizontal: 18,
    shadowColor: '#000', shadowOpacity: 0.5, shadowRadius: 18, shadowOffset: { width: 0, height: 6 }, elevation: 8,
  },
  resumeText: { color: '#fff', fontSize: 13, fontWeight: '700' },
});
